import axios from 'axios';
import { getCookie } from '../_helpers/cookie';
import { isDemoLocked } from '../../utils/demoConfig';

const API_URL = process.env.REACT_APP_API_URL_BASE;
const X_API_KEY = process.env.REACT_APP_X_API_KEY;
const DEMO_WEBHOOK_BASE = 'https://api.example-label.test';

const buildHeaders = (token) => ({
  Authorization: `Bearer ${token || ''}`,
  'x-api-key': X_API_KEY,
});

let publicPlansCache = null;
let publicPlansPromise = null;

const normalizePublicPlans = (data) => {
  const items = data?.results || data || [];
  return items.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
};

export const stripeSubscriptionService = {
  getConfig,
  saveConfig,
  syncWebhook,
  buildWebhookUrl,
  listProducts,
  getProduct,
  createProduct,
  updateProduct,
  deleteProduct,
  listPublicPlans,
  listPublicPlansCached,
  clearPublicPlansCache,
  createCheckoutSession,
  updateTenantSubscriptions,
};

function getConfig(token) {
  const authToken = token || getCookie('token');
  return axios
    .get(`${API_URL}tenant-stripe-config/`, { headers: buildHeaders(authToken) })
    .then((response) => response.data);
}

function saveConfig(payload, token) {
  const authToken = token || getCookie('token');
  return axios
    .post(`${API_URL}tenant-stripe-config/`, payload, {
      headers: { ...buildHeaders(authToken), 'Content-Type': 'application/json' },
    })
    .then((response) => response.data);
}

function syncWebhook(token) {
  const authToken = token || getCookie('token');
  return axios
    .post(`${API_URL}tenant-stripe-config/sync-webhook/`, {}, {
      headers: buildHeaders(authToken),
    })
    .then((response) => response.data);
}

function buildWebhookUrl(tenantId) {
  if (!tenantId) return '';
  if (isDemoLocked()) {
    return `${DEMO_WEBHOOK_BASE}/stripe-webhook/${tenantId}/`;
  }
  const base = (API_URL || '').replace(/\/+$/, '');
  return `${base}/wl-tenant-stripe-webhook/${tenantId}/`;
}

function listProducts(token) {
  const authToken = token || getCookie('token');
  return axios
    .get(`${API_URL}tenant-stripe-products/`, { headers: buildHeaders(authToken) })
    .then((response) => response.data);
}

function getProduct(id, token) {
  const authToken = token || getCookie('token');
  return axios
    .get(`${API_URL}tenant-stripe-products/${id}/`, { headers: buildHeaders(authToken) })
    .then((response) => response.data);
}

function createProduct(payload, token) {
  const authToken = token || getCookie('token');
  return axios
    .post(`${API_URL}tenant-stripe-products/`, payload, {
      headers: { ...buildHeaders(authToken), 'Content-Type': 'application/json' },
    })
    .then((response) => response.data);
}

function updateProduct(id, payload, token) {
  const authToken = token || getCookie('token');
  return axios
    .patch(`${API_URL}tenant-stripe-products/${id}/`, payload, {
      headers: { ...buildHeaders(authToken), 'Content-Type': 'application/json' },
    })
    .then((response) => response.data);
}

function deleteProduct(id, token) {
  const authToken = token || getCookie('token');
  return axios
    .delete(`${API_URL}tenant-stripe-products/${id}/`, { headers: buildHeaders(authToken) })
    .then((response) => response.data);
}

function listPublicPlans(token) {
  const authToken = token || getCookie('token');
  return axios
    .get(`${API_URL}tenant-stripe-plans/`, { headers: buildHeaders(authToken) })
    .then((response) => response.data);
}

function listPublicPlansCached(token) {
  if (publicPlansCache) {
    return Promise.resolve(publicPlansCache);
  }

  if (!publicPlansPromise) {
    publicPlansPromise = listPublicPlans(token)
      .then((data) => {
        publicPlansCache = normalizePublicPlans(data);
        return publicPlansCache;
      })
      .catch((error) => {
        publicPlansPromise = null;
        throw error;
      });
  }

  return publicPlansPromise;
}

function clearPublicPlansCache() {
  publicPlansCache = null;
  publicPlansPromise = null;
}

function createCheckoutSession(productId, token) {
  const authToken = token || getCookie('token');
  const origin = window.location.origin;
  return axios
    .post(`${API_URL}tenant-stripe-products/${productId}/checkout-session/`, {
      success_url: `${origin}/subscription/payment/success`,
      cancel_url: `${origin}/subscription/payment/failure`,
    }, {
      headers: buildHeaders(authToken),
    })
    .then((response) => response.data);
}

function updateTenantSubscriptions(payload, token) {
  const authToken = token || getCookie('token');
  return axios
    .post(`${API_URL}tenant-branding-update/`, payload, {
      headers: { ...buildHeaders(authToken), 'Content-Type': 'application/json' },
    })
    .then((response) => response.data);
}
